import {BulletedList} from 'shells/bulleted-list/bulleted-list';
import {colors} from 'shells/colors/colors';
import {Stack} from 'shells/layout/stack/stack';
import {ProgressBar} from 'shells/progress-bar/progress-bar';
import {StatusBadge} from 'shells/status-badge/status-badge';
import {Body} from 'shells/typography/body';

import {getBadgeLevel} from './helpers';

import styles from './ai-feature-gate-modal.module.css';

export type Label = 'Current plan' | 'Recommended';
export type Variant = 'default' | 'highlighted';

interface Props {
    planPill: React.ReactNode;
    label?: Label;
    usageText?: string;
    usageCurrent?: number;
    usageLimit?: number;
    usageLimitColor?: 'regular' | 'highlighted';
    footerText?: string;
    pricePerUser?: string;
    priceUnit?: string;
    bullets?: string[];
    variant?: Variant;
}

export function PlanCard(props: Props) {
    const {variant = 'default'} = props;

    const clampedUsageCurrent =
        props.usageCurrent != null && props.usageLimit != null
            ? Math.min(props.usageCurrent, props.usageLimit)
            : props.usageCurrent;

    const usagePercent =
        clampedUsageCurrent != null && props.usageLimit
            ? Math.min(clampedUsageCurrent / props.usageLimit, 1)
            : undefined;

    const cardClassName =
        variant === 'highlighted' ? styles['plan-card--highlighted'] : styles['plan-card'];

    return (
        <div className={cardClassName}>
            <Stack spacing={8}>
                <Stack
                    isHorizontal={true}
                    spacing={8}
                    alignItems='center'
                    justifyContent='space-between'
                    isFullWidth={true}
                >
                    {props.planPill}
                    {props.label && (
                        <StatusBadge
                            level={getBadgeLevel(props.label, variant)}
                            text={props.label}
                            size='small'
                        />
                    )}
                </Stack>
                {(props.usageText ||
                    (props.usageCurrent != null && props.usageLimit != null) ||
                    props.footerText) && (
                    <div className={styles['plan-card-content']}>
                        <Stack spacing={4}>
                            {props.usageText && (
                                <Body size='small' color='dark'>
                                    {props.usageText}
                                </Body>
                            )}
                            {props.usageCurrent != null && props.usageLimit != null && (
                                <>
                                    <Body size='large' isBold={true} color='dark'>
                                        {clampedUsageCurrent?.toLocaleString()}
                                        <Body
                                            size='large'
                                            color={
                                                props.usageLimitColor === 'highlighted'
                                                    ? 'ai-purple-dk'
                                                    : 'dark'
                                            }
                                            isBold={props.usageLimitColor === 'highlighted'}
                                        >
                                            {' / '}
                                            {props.usageLimit.toLocaleString()}
                                        </Body>
                                    </Body>
                                    <div className='py-4'>
                                        <ProgressBar
                                            percentComplete={usagePercent}
                                            height={4}
                                            color={colors.aiPurplePrimary}
                                            roundedCorners={true}
                                            backgroundColor={colors.aiPurplePinkLt}
                                        />
                                    </div>
                                </>
                            )}
                            {props.footerText && (
                                <Body size='small' color='dark'>
                                    {props.footerText}
                                </Body>
                            )}
                        </Stack>
                    </div>
                )}
                {props.pricePerUser && (
                    <Stack spacing={2}>
                        <Body size='xlarge' isBold={true} color='dark'>
                            {props.pricePerUser}
                        </Body>
                        {props.priceUnit && <Body>{props.priceUnit}</Body>}
                    </Stack>
                )}
                {props.bullets && props.bullets.length > 0 && (
                    <BulletedList
                        bullets={props.bullets}
                        marginBottom={0}
                        variant={variant === 'highlighted' ? 'purple' : undefined}
                    />
                )}
            </Stack>
        </div>
    );
}
